import { useState } from "react";
import { Mail, MapPin, Send, Github, Linkedin } from "lucide-react";
import { toast } from "sonner";
import emailjs from "@emailjs/browser";
import { personalInfo } from "../../data/mock";
import SectionHeading from "../SectionHeading";

// EmailJS identifiers (public by design). Abuse is limited by the honeypot
// below and by the domain allowlist configured in the EmailJS dashboard.
const EMAILJS = {
  SERVICE_ID: "service_88captm",
  TEMPLATE_ID: "template_34ceawj",
  PUBLIC_KEY: "eNrhYI2SqpYN9lHfX"
};

const emptyForm = { name: "", email: "", subject: "", message: "", _company: "" };

const Contact = () => {
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData._company) return; // bot trap

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in every field before sending.");
      return;
    }

    setIsSubmitting(true);
    try {
      await emailjs.send(
        EMAILJS.SERVICE_ID,
        EMAILJS.TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          subject: formData.subject,
          message: formData.message
        },
        { publicKey: EMAILJS.PUBLIC_KEY }
      );
      setFormData(emptyForm);
      toast.success("Message sent — I'll get back to you within a day.");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't send the message. Please email me directly instead.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialLinks = [
    { icon: Github, href: personalInfo.github, label: "GitHub" },
    { icon: Linkedin, href: personalInfo.linkedin, label: "LinkedIn" }
  ];

  const contactInfo = [
    { icon: Mail, label: "Email", value: personalInfo.email, href: `mailto:${personalInfo.email}` },
    { icon: MapPin, label: "Location", value: personalInfo.location, href: null }
  ];

  const inputClass =
    "w-full px-4 py-3 bg-panel2 border border-line rounded-md text-fg placeholder-fg-mute focus:outline-none focus:border-signal/60 transition-colors";
  const labelClass = "block font-mono text-xs uppercase tracking-wider text-fg-mute mb-2";

  return (
    <section id="contact" className="relative py-24 sm:py-28">
      <div className="rule mb-24" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading index="04" eyebrow="Contact" title="Get in" accent="touch">
          Have a system that needs building — or fixing? I'm open to backend work,
          ERP/MES projects, and technical collaboration.
        </SectionHeading>

        <div className="mt-16 grid lg:grid-cols-12 gap-8 items-start">
          {/* Coordinates */}
          <div className="lg:col-span-5 space-y-4">
            {contactInfo.map((info) => {
              const Row = info.href ? "a" : "div";
              return (
                <Row
                  key={info.label}
                  {...(info.href ? { href: info.href } : {})}
                  className="group flex items-center gap-4 bg-panel/70 backdrop-blur-sm border border-line rounded-lg p-5 hover:border-signal/40 transition-colors"
                >
                  <div className="p-3 rounded-md bg-panel2 border border-line group-hover:border-signal/40 transition-colors">
                    <info.icon className="w-5 h-5 text-signal" />
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-wider text-fg-mute">
                      {info.label}
                    </p>
                    <p className="text-fg font-medium group-hover:text-signal transition-colors">
                      {info.value}
                    </p>
                  </div>
                </Row>
              );
            })}

            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3.5 bg-panel border border-line rounded-md text-fg-dim hover:text-signal hover:border-signal/40 transition-colors"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>

            <div className="relative bg-panel/70 backdrop-blur-sm border border-line rounded-lg p-6 overflow-hidden">
              <div className="flex items-center gap-2.5 mb-2">
                <span className="live-dot w-2 h-2 rounded-full bg-live" />
                <span className="font-mono text-xs uppercase tracking-wider text-live">
                  Available for work
                </span>
              </div>
              <p className="text-sm text-fg-dim leading-relaxed">
                Currently open to backend engineering and ERP/MES projects. Typical
                reply time: under 24 hours.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              className="bg-panel/70 backdrop-blur-sm border border-line rounded-lg p-8 space-y-6"
            >
              <input
                type="text"
                name="_company"
                value={formData._company}
                onChange={handleInputChange}
                tabIndex={-1}
                autoComplete="off"
                className="hidden"
                aria-hidden="true"
              />

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className={labelClass}>Full name</label>
                  <input id="name" name="name" type="text" required value={formData.name}
                    onChange={handleInputChange} className={inputClass} placeholder="Your name" />
                </div>
                <div>
                  <label htmlFor="email" className={labelClass}>Email</label>
                  <input id="email" name="email" type="email" required value={formData.email}
                    onChange={handleInputChange} className={inputClass} placeholder="you@company.com" />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className={labelClass}>Subject</label>
                <input id="subject" name="subject" type="text" required value={formData.subject}
                  onChange={handleInputChange} className={inputClass} placeholder="What's this about?" />
              </div>

              <div>
                <label htmlFor="message" className={labelClass}>Message</label>
                <textarea id="message" name="message" rows={5} required value={formData.message}
                  onChange={handleInputChange} className={`${inputClass} resize-none`}
                  placeholder="Tell me about the project or the problem you're solving..." />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-signal text-ink rounded-md font-semibold hover:bg-signal-strong transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-ink border-t-transparent rounded-full animate-spin" />
                    Sending
                  </>
                ) : (
                  <>
                    Send message
                    <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
